import React, { useState, useEffect } from "react";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Autocomplete,
  TextField,
  Button,
  Checkbox,
  Paper
} from "@mui/material";
import speciesData from "./data/species.json";
import talentsData from "./data/talents.json"
import skillsData from "./data/skills.json";
import MarkedText from "./Util/MarkedText";
import { getEffective } from "./Util/applyHomebrew";
import formatSource from "./Util/FormatSource";
import StatForm from "./Util/StatForm";
import { adjustCredits } from "./Util/Credits";
import gear from "./data/gear.json"
import armor from "./data/armor.json"
import { toModList, describeSkillMod } from "./Util/SpeciesSkillMods";

let speciesList = speciesData
let talents = talentsData
let skills = skillsData

function DisplaySkillTraining({ skillTraining, character, handleCharacterChange, optionKey }) {
  const isOptionBased = !!optionKey;
  const storedSkills = isOptionBased
    ? (character.Species.OptionSelectedSkills?.[optionKey] || [])
    : (character.Species.SelectedSkills || []);
  const [selectedSkills, setSelectedSkills] = useState(storedSkills);

  useEffect(() => {
    setSelectedSkills(isOptionBased
      ? (character.Species.OptionSelectedSkills?.[optionKey] || [])
      : (character.Species.SelectedSkills || []));
  }, [character.Species.SelectedSkills, character.Species.OptionSelectedSkills]);

  let trainingCategory = [...skills];
  if (skillTraining.Requirement) {
    if (skillTraining.Requirement.SkillType && skillTraining.Requirement.SkillType !== "All") {
      trainingCategory = [...skills].filter((skill) => skill.type === skillTraining.Requirement.SkillType);
    }
    let careerSkills = character.CareerSkills.concat(character.SpecSkills || [], character.ExtraCareerSkills || []);
    if (skillTraining.Requirement.Career) {
      trainingCategory = [...skills].filter((skill) => careerSkills.includes(skill.skill));
    }
    if (skillTraining.Requirement.NonCareer) {
      trainingCategory = [...skills].filter((skill) => !careerSkills.includes(skill.skill));
    }
  }

  const handleCheckboxChange = (skillName) => {
    let updatedSkills;
    if (selectedSkills.includes(skillName)) {
      updatedSkills = selectedSkills.filter(skill => skill !== skillName);
    } else {
      if (selectedSkills.length < parseInt(skillTraining.SkillCount)) {
        updatedSkills = [...selectedSkills, skillName];
      } else {
        return;
      }
    }
    setSelectedSkills(updatedSkills);
    if (isOptionBased) {
      handleCharacterChange({
        Species: {
          ...character.Species,
          OptionSelectedSkills: {
            ...(character.Species.OptionSelectedSkills || {}),
            [optionKey]: updatedSkills
          }
        }
      });
    } else {
      handleCharacterChange({
        Species: {
          ...character.Species,
          SelectedSkills: updatedSkills
        }
      });
    }
  };

  return (
    <>
      {(isOptionBased || character.Species.Key !== "DROID") && trainingCategory.map((skill) => (
        <Grid container key={skill.skill}>
          <Grid item>
            <Checkbox
              size="small"
              sx={{ padding: "1px" }}
              checked={selectedSkills.includes(skill.skill)}
              onChange={() => handleCheckboxChange(skill.skill)}
              disabled={!selectedSkills.includes(skill.skill) && selectedSkills.length >= parseInt(skillTraining.SkillCount)}
            />
          </Grid>
          <Grid item>
            <Typography>{skill.skill}</Typography>
          </Grid>
        </Grid>
      ))}
    </>
  );
}

function DisplayOptions({
  character,
  handleCharacterChange,
  OptionChoices,
  SubSpeciesOption,
  SubSpecies,
}) {
  let optionState = "";
  let characterOptions = [];
  if (SubSpeciesOption) {

    characterOptions = character.Species.SubSpeciesList.find(
      (ss) => ss.Key === SubSpecies,
    ).OptionChoices;
  } else {
    characterOptions = character.Species.OptionChoices;
  }
  if (!OptionChoices) {
  } else if (Array.isArray(characterOptions)) {
    optionState = [];
    for (let o in characterOptions) {
      if (characterOptions[o].Selected !== undefined) {
        optionState.push(characterOptions[o].Selected);
      } else {
        optionState.push("");
      }
    }
  } else if (characterOptions && Array.isArray(characterOptions.Options)) {
    if (characterOptions.Selected !== undefined) {
      optionState = characterOptions.Selected;
    } else {
      optionState = "";
    }
  }
  const [selectedSpeciesOption, setSelectedSpeciesOption] =
    useState(optionState);
  const handleSpeciesOptionChange = (index, selectedValue) => {
    let xpAdjust = 0;
    let xpAdjustRefund = -character.Species.StartingXPAdjust || 0;
    let newCharAdjust = {};
    if (Array.isArray(characterOptions)) {
      // Sum XP and char adjustments from ALL selected options after this change
      characterOptions.forEach((choice, i) => {
        const effectiveSelection = i === index ? selectedValue : choice.Selected;
        if (!effectiveSelection || !Array.isArray(choice.Options)) return;
        // Support both single-select (string) and checklist (array) selections
        const selectedKeys = Array.isArray(effectiveSelection) ? effectiveSelection : [effectiveSelection];
        selectedKeys.forEach(key => {
          const selectedOpt = choice.Options.find(o => o.Key === key);
          if (selectedOpt?.StartingAttributes?.Experience) {
            xpAdjust += parseInt(selectedOpt.StartingAttributes.Experience) || 0;
          }
          if (selectedOpt?.StartingChars) {
            for (const [char, val] of Object.entries(selectedOpt.StartingChars)) {
              newCharAdjust[char] = (newCharAdjust[char] || 0) + parseInt(val);
            }
          }
        });
      });
      setSelectedSpeciesOption((prevSelectedOptions) => ({
        ...prevSelectedOptions,
        [index]: selectedValue,
      }));
    } else {
      if (Array.isArray(characterOptions.Options) && characterOptions.Options.find((o) => o.Key === selectedValue).StartingAttributes) {
        xpAdjust += parseInt(characterOptions.Options.find((o) => o.Key === selectedValue).StartingAttributes.Experience) || 0;
      }
      setSelectedSpeciesOption(selectedValue);
    }

    // Apply characteristic delta between previous and new option selections
    const oldCharAdjust = character.Species.StartingCharsAdjust || {};
    const newCharacteristics = { ...character.Characteristics };
    let hasCharDelta = false;
    ['Brawn', 'Agility', 'Intellect', 'Cunning', 'Willpower', 'Presence'].forEach(c => {
      const speciesBase = parseInt(character.Species.StartingChars?.[c] || 0);
      const current = parseInt(newCharacteristics[c] || 0);
      const purchased = Math.max(0, current - speciesBase - (oldCharAdjust[c] || 0));
      const capped = Math.min(5, speciesBase + purchased + (newCharAdjust[c] || 0));
      if (capped !== current) {
        newCharacteristics[c] = String(capped);
        hasCharDelta = true;
      }
    });

    let Species;
    if (!SubSpeciesOption) {
      if (characterOptions.Name) {
        Species = {
          ...character.Species,
          OptionChoices: {
            ...character.Species.OptionChoices,
            Selected: selectedValue,
          },
          StartingXPAdjust: xpAdjust,
          SelectedSkills: []
        };
      } else if (Array.isArray(characterOptions)) {
        const updatedOptionChoices = characterOptions.map(
          (choice, i) => {
            if (index === i) {
              return { ...choice, Selected: selectedValue };
            }
            return choice;
          },
        );
        const oldSelected = characterOptions[index]?.Selected;
        const optionSelectedSkills = { ...(character.Species.OptionSelectedSkills || {}) };
        if (Array.isArray(oldSelected) && Array.isArray(selectedValue)) {
          // Checklist: remove OptionSelectedSkills for any deselected options
          oldSelected.filter(k => !selectedValue.includes(k)).forEach(k => { delete optionSelectedSkills[k]; });
        } else if (oldSelected && !Array.isArray(oldSelected) && oldSelected !== selectedValue) {
          delete optionSelectedSkills[oldSelected];
        }
        Species = {
          ...character.Species,
          OptionChoices: updatedOptionChoices,
          StartingXPAdjust: xpAdjust,
          StartingCharsAdjust: newCharAdjust,
          OptionSelectedSkills: optionSelectedSkills,
          SelectedSkills: []
        };
      } else if (characterOptions.Options?.Name) {
        Species = {
          ...character.Species,
          OptionChoices: {
            ...character.Species.OptionChoices,
            Options: {
              ...character.Species.OptionChoices.Options,
              Selected: selectedValue,
            },
          },
          StartingXPAdjust: xpAdjust,
          SelectedSkills: []
        };
      }
    } else {
      const updatedOptionChoices = character.Species.SubSpeciesList.find(
        (ss) => ss.Key === SubSpecies,
      ).OptionChoices.map((option, i) => {
        if (index === i) {
          return { ...option, Selected: selectedValue };
        }
        return option;
      });
      Species = {
        ...character.Species,
        SubSpeciesList: [
          ...character.Species.SubSpeciesList.map((ss, i) => {
            if (SubSpecies === character.Species.SubSpeciesList[i].Key) {
              return { ...ss, OptionChoices: updatedOptionChoices };
            }
            return ss;
          }),
        ],
        SelectedSkills: []
      };
    }
    handleCharacterChange({
      Species,
      XP: character.XP + xpAdjustRefund + xpAdjust,
      ...(hasCharDelta ? { Characteristics: newCharacteristics } : {})
    });
  };
  let descriptions = [];
  if (!OptionChoices) {
    return;
  } else if (Array.isArray(OptionChoices)) {
    descriptions = OptionChoices.map((item, index) => (
      <div key={item.Key}>
        {item.Options.Name && (
          <Typography key={item.Key + index}>
            <b>{item.Options.Name}</b>
            <br />
          </Typography>
        )}
        {item.Options.Description && (
          <span
            dangerouslySetInnerHTML={{
              __html: MarkedText.renderer(item.Options.Description),
            }}
          />
        )}
        {item.Options.StartingSkillTraining && (
          <DisplaySkillTraining skillTraining={item.Options.StartingSkillTraining.SkillTraining} character={character} handleCharacterChange={handleCharacterChange} />
        )}
        {item.DisplayMode === "checklist" ? (() => {
          const selected = Array.isArray(character.Species.OptionChoices?.[index]?.Selected)
            ? character.Species.OptionChoices[index].Selected
            : [];
          return item.Options.map(option => {
            const isChecked = selected.includes(option.Key);
            return (
              <Paper key={option.Key} variant="outlined" sx={{ p: 1, mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                <Checkbox
                  size="small"
                  sx={{ pt: 0.5, pb: 0.5, pr: 1, flexShrink: 0 }}
                  checked={isChecked}
                  onChange={() => {
                    let newSelected;
                    if (isChecked) {
                      newSelected = selected.filter(k => k !== option.Key);
                    } else if (option.ExclusiveGroup) {
                      const exclusive = item.Options.filter(o => o.ExclusiveGroup === option.ExclusiveGroup).map(o => o.Key);
                      newSelected = [...selected.filter(k => !exclusive.includes(k)), option.Key];
                    } else {
                      newSelected = [...selected, option.Key];
                    }
                    handleSpeciesOptionChange(index, newSelected);
                  }}
                />
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle2"><b>{option.Name}</b></Typography>
                  <span dangerouslySetInnerHTML={{ __html: MarkedText.renderer(option.Description) }} />
                  <DisplaySkills SkillModifiers={option.SkillModifiers} />
                  <DisplayTalents TalentModifiers={option.TalentModifiers} />
                  {option.StartingSkillTraining && isChecked && (
                    <DisplaySkillTraining
                      skillTraining={option.StartingSkillTraining.SkillTraining}
                      character={character}
                      handleCharacterChange={handleCharacterChange}
                      optionKey={option.Key}
                    />
                  )}
                </div>
              </Paper>
            );
          });
        })() : item.Options[1] ? (
          <FormControl fullWidth>
            <InputLabel>{item.Name}</InputLabel>
            <Select
              value={selectedSpeciesOption[index] || ""}
              name={item.Name}
              onChange={(e) => handleSpeciesOptionChange(index, e.target.value)}
            >
              {item.Options.map((subItem, subIndex) => (
                <MenuItem key={subItem.Key} value={subItem.Key}>
                  <b>{subItem.Name}</b>
                </MenuItem>
              ))}
            </Select>
            {character.Species.OptionChoices[index] && item.Options.find((o) => o.Key === character.Species.OptionChoices[index].Selected) && (
              <>
                <span
                  dangerouslySetInnerHTML={{
                    __html: MarkedText.renderer(
                      item.Options.find((o) => o.Key === character.Species.OptionChoices[index].Selected).Description,
                    ),
                  }}
                />
                {item.Options.find((o) => o.Key === character.Species.OptionChoices[index].Selected).StartingSkillTraining && (
                  <DisplaySkillTraining skillTraining={item.Options.find((o) => o.Key === character.Species.OptionChoices[index].Selected).StartingSkillTraining.SkillTraining} character={character} handleCharacterChange={handleCharacterChange} optionKey={character.Species.OptionChoices[index].Selected} />
                )}
              </>)}
          </FormControl>
        ) : null}
        {item.Options[0] && !item.Options[1] &&
          <div key={item.Options[0].Key}>
            <Typography variant="h6">{item.Options[0].Name}</Typography>
            <span
              dangerouslySetInnerHTML={{
                __html: MarkedText.renderer(
                  item.Options[0].Description,
                ),
              }}
            />
          </div>
        }
      </div>
    ));
  } else if (Array.isArray(OptionChoices.Options)) {
    let chosen = {}
    if (selectedSpeciesOption) {
      chosen = OptionChoices.Options.find(
        (option) => option.Key === selectedSpeciesOption
      )
    }
    descriptions = (
      <FormControl fullWidth>
        <InputLabel>Select Species Option</InputLabel>
        <Select
          value={selectedSpeciesOption || ""}
          onChange={(e) =>
            handleSpeciesOptionChange(OptionChoices.Key, e.target.value)
          }
        >
          {OptionChoices.Options.map((item, index) => (
            <MenuItem key={item.Key} value={item.Key}>
              <b>{item.Name}</b>
            </MenuItem>
          ))}
        </Select>
        {chosen && (
          <>
            <span
              dangerouslySetInnerHTML={{
                __html: MarkedText.renderer(
                  chosen.Description
                ),
              }}
            />
            {chosen.StartingSkillTraining && (
              <DisplaySkillTraining
                skillTraining={chosen.StartingSkillTraining.SkillTraining}
                character={character}
                handleCharacterChange={handleCharacterChange}
              />
            )}
          </>
        )}
      </FormControl>
    );
  } else if (OptionChoices.Description) {
    descriptions = (
      <div key={OptionChoices.Key}>
        <Typography>
          <b>{OptionChoices.Name}:</b>
          <br />
          <span
            dangerouslySetInnerHTML={{
              __html: MarkedText.renderer(OptionChoices.Description),
            }}
          />
        </Typography>
        {OptionChoices.StartingSkillTraining && (
          <DisplaySkillTraining skillTraining={OptionChoices.StartingSkillTraining.SkillTraining} character={character} handleCharacterChange={handleCharacterChange} />
        )}
      </div>
    );
  } else if (OptionChoices.Options.Description) {
    descriptions = (<>
      <Typography key={OptionChoices.Options.Key}>
        <b>{OptionChoices.Options.Name}:</b>
        <br />
        <span
          dangerouslySetInnerHTML={{
            __html: MarkedText.renderer(OptionChoices.Options.Description),
          }}
        />
      </Typography>
      {OptionChoices.Options.StartingSkillTraining && (
        <DisplaySkillTraining skillTraining={OptionChoices.Options.StartingSkillTraining.SkillTraining} character={character} handleCharacterChange={handleCharacterChange} />
      )}
    </>);
  }
  return <div>{descriptions}</div>;
}

function DisplaySubSpecies({
  SubSpeciesList,
  character,
  handleCharacterChange,
  setSelectedSpecies
}) {
  const handleSubSpeciesChange = (event) => {
    let newSpecies = { ...character.Species, SubSpecies: event.target.value }
    setSelectedSpecies(newSpecies)
    handleCharacterChange({
      Species: newSpecies,
    });
  };
  let descriptions = [];
  if (SubSpeciesList === undefined) {
    return;
  } else if (Array.isArray(SubSpeciesList)) {
    descriptions = (
      <div>
        <FormControl fullWidth>
          <InputLabel>Select Sub-Species</InputLabel>
          <Select
            value={character.Species.SubSpecies || ""}
            onChange={handleSubSpeciesChange}
          >
            {SubSpeciesList.map((subSpecies, index) => (
              <MenuItem key={subSpecies.Key} value={subSpecies.Key}>
                {subSpecies.Name}
              </MenuItem>
            ))}
          </Select>
          {character.Species.SubSpecies && (
            <Typography
              dangerouslySetInnerHTML={{
                __html: MarkedText.renderer(
                  SubSpeciesList.find(
                    (ss) => ss.Key === character.Species.SubSpecies,
                  )?.Description || "",
                ),
              }}
            />
          )}
          {SubSpeciesList.find(
            (ss) => ss.Key === character.Species.SubSpecies,
          ) && (
              <DisplayOptions
                character={character}
                handleCharacterChange={handleCharacterChange}
                OptionChoices={
                  character.Species.SubSpeciesList.find(
                    (ss) => ss.Key === character.Species.SubSpecies,
                  ).OptionChoices
                }
                SubSpeciesOption={true}
                SubSpecies={character.Species.SubSpecies}
              />
            )}
        </FormControl>
        {SubSpeciesList.find(
          (ss) => ss.Key === character.Species.SubSpecies,
        ) && (
            <div>
              <DisplaySkills
                SkillModifiers={
                  SubSpeciesList.find(
                    (ss) => ss.Key === character.Species.SubSpecies,
                  ).SkillModifiers
                }
              />
              <DisplayTalents
                TalentModifiers={
                  SubSpeciesList.find(
                    (ss) => ss.Key === character.Species.SubSpecies,
                  ).TalentModifiers
                }
              />{" "}
            </div>
          )}
      </div>
    );
  }
  return <div>{descriptions}</div>;
}

function DisplaySkills({ SkillModifiers }) {
  return toModList(SkillModifiers).flatMap((mod) => describeSkillMod(mod, skills)).map((line, i) => (
    <Typography key={i}>{line}</Typography>
  ));
}

function DisplayTalents({ TalentModifiers }) {
  let descriptions = [];
  if (!TalentModifiers) {
    return;
  }
  if (TalentModifiers.Key) {
    descriptions = (
      <Typography>
        Begins with {TalentModifiers.RankAdd} rank in{" "}
        {talents.find((t) => t.Key === TalentModifiers.Key).Name}.
      </Typography>
    );
  } else if (Array.isArray(TalentModifiers)) {
    descriptions = TalentModifiers.map((item, index) => (
      <div key={item.Key}>
        <Typography>
          Begins with {item.RankAdd} rank in{" "}
          {talents.find((t) => t.Key === item.Key).Name}.
        </Typography>
      </div>
    ));
  }
  return descriptions;
}
function getSpecies(key, speciesList) {
  return speciesList.findIndex((s) => s.Key === key);
}
function App({ character, handleCharacterChange, appFiles }) {
  speciesList = getEffective('species', speciesData);
  talents = getEffective('talents', talentsData);
  skills = getEffective('skills', skillsData);

  const [selectedSpecies, setSelectedSpecies] = useState(
    getSpecies(character.Species.Key, speciesList),
  );
  const [startingXp, setStartingXp] = useState(
    character.Species.StartingAttrs
      ? character.Species.StartingAttrs.Experience
      : 0,
  );
  const [selectedSpeciesData, setSelectedSpeciesData] = useState(
    speciesList[getSpecies(character.Species.Species, speciesList)],
  );
  const [minChars, setMinChars] = useState({
    Brawn: 0,
    Agility: 0,
    Intellect: 0,
    Cunning: 0,
    Willpower: 0,
    Presence: 0,
  });

  const handleChangeMinimum = (characteristic, value) => {
    let newMin = value >= 0 ? value : 0;
    newMin = newMin <= 4 ? newMin : 4;
    setMinChars({
      ...minChars,
      [characteristic]: newMin,
    });
  };

  const handleFilterOptions = (options, { inputValue }) => {
    return options.filter((option) => {
      if (!option.Name.toLowerCase().includes(inputValue.toLowerCase())) {
        return false;
      }
      for (const characteristic in minChars) {
        if (
          !option.StartingChars ||
          parseInt(option.StartingChars[characteristic], 10) <
          minChars[characteristic]
        ) {
          return false;
        }
      }
      return true;
    });
  };

  const handleSpeciesChange = (key) => {
    let charRebate = 0
    let currentChars = character.Characteristics
    let speciesChars = character.Species.StartingChars
    let currentCareerRanks = character.CareerRanks
    let currentSpecRanks = character.SpecRanks
    let currentKey = character.Species.Key
    let newGear = character.Gear
    let newArmor = character.Armor
    let newCredits = character.Credits
    if (currentKey === "DROID") {
      currentCareerRanks = []
      currentSpecRanks = []
    }

    if (key === "SKAKOAN") {
      newArmor.push(armor.find(a => a.Key === "SKAKPRESS"))
    }
    if (currentKey === "SKAKOAN") {
      newArmor = newArmor.filter(a => a.Key !== "SKAKPRESS")
    }
    if (key === "TOGNATHS") {
      newGear.push(gear.find(g => g.Key === "BREATHM"))
    }
    if (currentKey === "TOGNATHS") {
      newGear = newGear.filter(a => a.Key !== "BREATHM")
    }
    if (key === "MUUN") {
      newCredits = adjustCredits(newCredits, 1000)
    }
    if (currentKey === "MUUN") {
      newCredits = adjustCredits(newCredits, -1000)
    }

    if (character.Species) {
      (character.Species.StartingArmor || []).forEach((k) => { newArmor = newArmor.filter((a) => a.Key !== k) })
      ;(character.Species.StartingGear || []).forEach((k) => { newGear = newGear.filter((g) => g.Key !== k) })
      if (character.Species.StartingCredits) { newCredits = adjustCredits(newCredits, -(parseInt(character.Species.StartingCredits) || 0)) }
    }
    const incomingSpecies = key ? speciesList[getSpecies(key, speciesList)] : null
    if (incomingSpecies) {
      (incomingSpecies.StartingArmor || []).forEach((k) => { const a = armor.find((x) => x.Key === k); if (a) { newArmor.push(a) } })
      ;(incomingSpecies.StartingGear || []).forEach((k) => { const g = gear.find((x) => x.Key === k); if (g) { newGear.push(g) } })
      if (incomingSpecies.StartingCredits) { newCredits = adjustCredits(newCredits, parseInt(incomingSpecies.StartingCredits) || 0) }
    }

    charRebate -= character.Species.StartingXPAdjust ? parseInt(character.Species.StartingXPAdjust) : 0
    for (const [key, value] of Object.entries(currentChars)) {

      if (key in speciesChars) {
        let currentCharValue = parseInt(value, 10);
        const speciesCharValue = parseInt(speciesChars[key], 10);

        while (currentCharValue > speciesCharValue) {
          charRebate += currentCharValue * 10;
          currentCharValue--;
        }
      }
    }

    if (!key) {
      handleCharacterChange({
        XP: parseInt(character.XP, 10) + parseInt(0) - startingXp,
        Species: {
          Species: "",
          StartingAttrs: "",
        },
        Characteristics: {},
        UsedStartingXP: character.Extras?.ShortPath ? 30 : 0
      });
      setSelectedSpecies("");
      setSelectedSpeciesData("");
      setStartingXp(0);
    } else {
      var index = getSpecies(key, speciesList);
      setSelectedSpecies(key);
      let data = speciesList[index];
      setSelectedSpeciesData(data);
      setStartingXp(data === undefined ? 0 : data.StartingAttrs.Experience);
      handleCharacterChange({
        XP:
          charRebate +
          parseInt(character.XP, 10) +
          parseInt(data === undefined ? 0 : data.StartingAttrs.Experience, 10) -
          startingXp,
        Species: {
          ...data,
          Species: key,
          SubSpecies: "",
        },
        CareerRanks: currentCareerRanks,
        SpecRanks: currentSpecRanks,
        Characteristics: data.StartingChars,
        UsedStartingXP: character.Extras?.ShortPath ? 30 : 0,
        Gear: newGear,
        Armor: newArmor,
        Credits: newCredits
      });
    }
  };

  return (
    <Container>
      <Typography variant="h3">Species</Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mt: 2, mb: 2 }}>
        <Grid item xs={12} sm={12} md={3} lg={3}>
          <Typography>Minimum Characteristics Filter: </Typography>
        </Grid>
        {Object.keys(minChars).map((characteristic) => (
          <Grid item key={characteristic} xs={4} sm={4} md={1} lg={1}>
            <TextField
              sx={{ width: '100%', maxWidth: "120px" }}
              label={characteristic}
              type="number"
              value={minChars[characteristic]}
              onChange={(e) =>
                handleChangeMinimum(
                  characteristic,
                  parseInt(e.target.value, 10),
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        ))}
        <Grid item xs={12} sm={12} md={1} lg={1}>
          <Button
            fullWidth
            onClick={() =>
              setMinChars({
                Brawn: 0,
                Agility: 0,
                Intellect: 0,
                Cunning: 0,
                Willpower: 0,
                Presence: 0,
              })
            }
          >
            Clear
          </Button>
        </Grid>
      </Grid>
      <FormControl fullWidth>
        <Autocomplete
          disablePortal
          disabled={character.GMGrantMode}
          sx={{ paddingBottom: "10px" }}
          value={
            speciesList.some((option) => option.Key === selectedSpecies)
              ? selectedSpecies
              : null
          }
          onChange={(event, newValue, reason) => {
            handleSpeciesChange(newValue === null ? "" : newValue.Key);
            if (reason === 'selectOption') {
              const input = document.querySelector('.MuiAutocomplete-input');
              if (input) {
                input.blur();
              }
            }
          }}
          options={speciesList.sort((a, b) => { return a.Name.localeCompare(b.Name); })}
          getOptionLabel={(option) => option.Name || ""}
          isOptionEqualToValue={(option, value) => option.Key === value}
          filterOptions={handleFilterOptions}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            const statsAria = `Brawn ${option.StartingChars.Brawn}, Agility ${option.StartingChars.Agility}, Intellect ${option.StartingChars.Intellect}, Cunning ${option.StartingChars.Cunning}, Willpower ${option.StartingChars.Willpower}, Presence ${option.StartingChars.Presence}`;
            return (
              <li key={key} {...optionProps} aria-label={`${option.Name}, ${statsAria}`}>
                <Grid container alignItems="center" spacing={2} sx={{ width: '100%', px: 1 }}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1">
                      {option.Name}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <img
                      src={option.thumbnailUrl || option.imageUrl}
                      alt=""
                      style={{ height: 30, objectFit: 'contain' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7} sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", lg: "flex-end" }
                  }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      aria-hidden="true"
                    >
                      | {option.StartingChars.Brawn} | {option.StartingChars.Agility} | {option.StartingChars.Intellect} | {option.StartingChars.Cunning} | {option.StartingChars.Willpower} | {option.StartingChars.Presence} |
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Species"
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Select a character species',
              }}
            />
          )}
        />
      </FormControl>
      {selectedSpeciesData && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h4">{selectedSpeciesData.Name}</Typography>
                <Grid container>
                  <Grid item xs={4} lg={2}>
                    <StatForm title="Brawn" color="red" value={String(Math.min(5, parseInt(selectedSpeciesData.StartingChars.Brawn || 0) + (selectedSpeciesData.Key === character.Species.Key ? (character.Species.StartingCharsAdjust?.Brawn || 0) : 0)))} sizes={["h6", "h4"]} />
                  </Grid>
                  <Grid item xs={4} lg={2}>
                    <StatForm title="Agility" color="red" value={String(Math.min(5, parseInt(selectedSpeciesData.StartingChars.Agility || 0) + (selectedSpeciesData.Key === character.Species.Key ? (character.Species.StartingCharsAdjust?.Agility || 0) : 0)))} sizes={["h6", "h4"]} />
                  </Grid>
                  <Grid item xs={4} lg={2}>
                    <StatForm title="Intellect" color="red" value={String(Math.min(5, parseInt(selectedSpeciesData.StartingChars.Intellect || 0) + (selectedSpeciesData.Key === character.Species.Key ? (character.Species.StartingCharsAdjust?.Intellect || 0) : 0)))} sizes={["h6", "h4"]} />
                  </Grid>
                  <Grid item xs={4} lg={2}>
                    <StatForm title="Cunning" color="red" value={String(Math.min(5, parseInt(selectedSpeciesData.StartingChars.Cunning || 0) + (selectedSpeciesData.Key === character.Species.Key ? (character.Species.StartingCharsAdjust?.Cunning || 0) : 0)))} sizes={["h6", "h4"]} />
                  </Grid>
                  <Grid item xs={4} lg={2}>
                    <StatForm title="Willpower" color="red" value={String(Math.min(5, parseInt(selectedSpeciesData.StartingChars.Willpower || 0) + (selectedSpeciesData.Key === character.Species.Key ? (character.Species.StartingCharsAdjust?.Willpower || 0) : 0)))} sizes={["h6", "h4"]} />
                  </Grid>
                  <Grid item xs={4} lg={2}>
                    <StatForm title="Presence" color="red" value={String(Math.min(5, parseInt(selectedSpeciesData.StartingChars.Presence || 0) + (selectedSpeciesData.Key === character.Species.Key ? (character.Species.StartingCharsAdjust?.Presence || 0) : 0)))} sizes={["h6", "h4"]} />
                  </Grid>
                </Grid>
              </CardContent>
              <CardContent>
                <Typography variant="h7">
                  <b>Starting Experience:</b>{" "}
                  {selectedSpeciesData.Key === character.Species.Key
                    ? String(parseInt(selectedSpeciesData.StartingAttrs.Experience || 0) + (character.Species.StartingXPAdjust || 0))
                    : selectedSpeciesData.StartingAttrs.Experience}
                </Typography>{" "}
                <br />
                <Typography variant="h7">
                  <b>Wound Threshold:</b>{" "}
                  {selectedSpeciesData.StartingAttrs.WoundThreshold} + Brawn
                </Typography>{" "}
                <br />
                <Typography variant="h7">
                  <b>Strain Threshold:</b>{" "}
                  {selectedSpeciesData.StartingAttrs.StrainThreshold} +
                  Willpower
                </Typography>
              </CardContent>
              <CardContent>
                <DisplaySkills
                  SkillModifiers={selectedSpeciesData.SkillModifiers}
                />
                <DisplayTalents
                  TalentModifiers={selectedSpeciesData.TalentModifiers}
                />
                <DisplayOptions
                  OptionChoices={selectedSpeciesData.OptionChoices}
                  character={character}
                  handleCharacterChange={handleCharacterChange}
                />
                <DisplaySubSpecies
                  SubSpeciesList={selectedSpeciesData.SubSpeciesList}
                  character={character}
                  handleCharacterChange={handleCharacterChange}
                  setSelectedSpecies={setSelectedSpecies}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardMedia
                height="600"
                component="img"
                className="speciesImage"
                image={selectedSpeciesData.imageUrl}
                title={selectedSpeciesData.Name}
                sx={{ padding: "1em 1em 0 1em", objectFit: "contain" }}
              />
            </Card>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Card>
              <CardContent>
                <div
                  style={{ padding: "15px", fontSize: "0.8em" }}
                  dangerouslySetInnerHTML={{
                    __html: MarkedText.renderer(
                      selectedSpeciesData.Description,
                    ),
                  }}
                />
                <Typography>{formatSource(selectedSpeciesData.Source)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default App;
